import { useState, useRef, useCallback, useEffect } from 'react';
const PICKER_HANDLED_KEYS = ['Escape', 'Enter', 'Tab', 'ArrowUp', 'ArrowDown'];

const AT_MENTION_RE = /@([a-zA-Z0-9_]*)$/;

export type MentionState = {
    isOpen: boolean;
    anchorEl: HTMLSpanElement | null;
    query: string;
    onJoditKeyup: (e: Event) => void;
    close: () => void;
};

export function useMentionDetection(): MentionState {
    const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);
    const [query, setQuery] = useState('');

    const anchorElRef = useRef<HTMLSpanElement | null>(null);

    const isOpenRef = useRef(false);

    const isOpen = anchorEl !== null;

    const close = useCallback(() => {
        const phantom = anchorElRef.current;
        if (phantom) {
            try { phantom.parentNode?.removeChild(phantom); }
            catch { }
            anchorElRef.current = null;
        }
        isOpenRef.current = false;
        setAnchorEl(null);
        setQuery('');
    }, []);

    const detect = useCallback(() => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        const textNode = range.startContainer;
        const offset = range.startOffset;

        const textBefore = (
            textNode.nodeType === Node.TEXT_NODE ? textNode.textContent ?? '' : ''
        ).slice(0, offset);

        const match = textBefore.match(AT_MENTION_RE);

        if (match) {
            setQuery(match[0]);
            isOpenRef.current = true;

            if (!anchorElRef.current) {
                const phantom = document.createElement('span');
                phantom.style.cssText = 'display:inline;width:0;height:0;font-size:0;';
                try {
                    const caretRange = range.cloneRange();
                    caretRange.collapse(true);
                    caretRange.insertNode(phantom);
                    anchorElRef.current = phantom;
                    setAnchorEl(phantom);
                }
                catch { }
            }
        } else if (isOpenRef.current) {
            close();
        }
    }, [close]);

    const detectRef = useRef(detect);
    useEffect(() => { detectRef.current = detect; }, [detect]);

    const onJoditKeyup = useCallback((e: Event) => {
        const key = (e as KeyboardEvent).key;
        if (!PICKER_HANDLED_KEYS.includes(key)) {
            detectRef.current();
        }
    }, []);

    return { isOpen, anchorEl, query, onJoditKeyup, close };
}
